import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../../store';
import {
    ProjectContentComment,
    createProjectContentComment,
    deleteProjectContentComment,
    getProjectContentComments,
} from '../../../../features/sessions/sessionProjectManagementPlayer/sliceProjectManagementPlayer';
import { AuthHeaders } from '../../../../#interfaces/interfaces';
import {
    Box, Button, CircularProgress, Dialog, DialogActions,
    DialogContent, DialogTitle, IconButton, TextField, Typography,
} from '@mui/material';
import { UserAvatar } from '../../../userAvatar/UserAvatar';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

const BG_SECTION     = '#1c1c24';
const BG_CARD        = '#24242e';
const BRAND_RED      = '#c0392b';
const TEXT_PRIMARY   = '#f4f4f6';
const TEXT_SECONDARY = 'rgba(244,244,246,0.65)';
const BORDER_SUBTLE  = '1px solid rgba(255,255,255,0.08)';

interface ProjectContentCommentsModalProps {
    projectContentShowedId: string;
    userNicknameRoute: string | undefined;
    currentUserNickname?: string;
    onCloseClick: (v: boolean) => void;
}

export function ProjectContentCommentsModal({
    projectContentShowedId,
    userNicknameRoute,
    currentUserNickname,
    onCloseClick,
}: ProjectContentCommentsModalProps) {
    const [inputComment, setInputComment]                   = useState('');
    const [projectContentCommentsPage, setProjectContentCommentsPage] = useState(1);

    const authHeaders    = useSelector((state: RootState) => state.session.authHeaders);
    const currentUser    = useSelector((state: RootState) => state.session.currentUser);
    const existingComments = useSelector((state: RootState) => state.sessionProjectPlayerManagement.currentProjectContentCommentsToShow);
    const newComments    = useSelector((state: RootState) => state.sessionProjectPlayerManagement.newCurrentProjectContentComments);
    const loadingMore    = useSelector((state: RootState) => state.sessionProjectPlayerManagement.lodingMoreProjectContensComments);

    const dispatch = useDispatch<AppDispatch>();

    useEffect(() => {
        if (!authHeaders || !currentUser?.nickname) return;
        dispatch(getProjectContentComments({
            headers: authHeaders,
            urlParams: {
                userNickname: currentUser.nickname,
                projectContentId: projectContentShowedId,
                projectContentCommentsPage: `${projectContentCommentsPage}`,
            },
        })).catch((err) => console.error('Erro ao buscar comentários:', err));
    }, [projectContentCommentsPage]);

    useEffect(() => {
        const handleScroll = () => {
            const el = document.getElementById('ul-project-content-comments-modal');
            if (!el) return;
            if (el.scrollTop + el.clientHeight >= el.scrollHeight && !loadingMore) {
                setProjectContentCommentsPage(p => p + 1);
            }
        };
        const timer = setTimeout(() => {
            const el = document.getElementById('ul-project-content-comments-modal');
            if (el) {
                el.addEventListener('scroll', handleScroll);
                return () => el.removeEventListener('scroll', handleScroll);
            }
        }, 100);
        return () => clearTimeout(timer);
    }, [loadingMore]);

    function handleFormatDate(dateString: string | undefined): string | null {
        if (!dateString) return null;
        return new Date(dateString).toLocaleString(navigator.language, {
            day: '2-digit', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
        });
    }

    async function handleDelete(
        commentId: string,
        projectContentId: string,
        headers: AuthHeaders | undefined,
        nickname: string | undefined,
    ) {
        if (!headers || !nickname) return;
        await dispatch(deleteProjectContentComment({
            headers,
            urlParams: { userNickname: nickname, projectContentId, projectContentCommentId: commentId },
        }));
    }

    async function handleCreate() {
        if (!authHeaders || !userNicknameRoute || !projectContentShowedId || !inputComment.trim()) return;
        const res = await dispatch(createProjectContentComment({
            headers: authHeaders,
            routeParams: { userNickname: userNicknameRoute, projectContentId: projectContentShowedId },
            body: { body: inputComment },
        }));
        if (res.meta.requestStatus === 'fulfilled') setInputComment('');
    }

    const allComments = [...newComments, ...existingComments];

    const renderComment = (item: ProjectContentComment, index: number) => (
        <Box
            key={index}
            sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                px: 2,
                py: 1,
                borderBottom: BORDER_SUBTLE,
                '&:last-child': { borderBottom: 'none' },
            }}
        >
            <UserAvatar src={item.user.userMoreInfo?.avatarUrl} size={30} sx={{ mt: 0.25 }} />

            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Box sx={{ flex: 1, bgcolor: 'rgba(255,255,255,0.05)', borderRadius: '6px', px: 1.25, py: 0.75 }}>
                        <Typography
                            variant='caption'
                            fontWeight={700}
                            color={TEXT_PRIMARY}
                            sx={{ display: 'block', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                        >
                            {item.user.userMoreInfo?.nickname}
                        </Typography>
                        <Typography variant='body2' color={TEXT_SECONDARY} sx={{ mt: 0.25, wordBreak: 'break-word' }}>
                            {item.body}
                        </Typography>
                    </Box>
                    <IconButton
                        size='small'
                        onClick={() => handleDelete(`${item.id}`, `${item.projectContentId}`, authHeaders, currentUser?.nickname)}
                        sx={{ color: 'rgba(255,100,100,0.6)', flexShrink: 0, '&:hover': { color: 'rgba(255,100,100,1)' } }}
                    >
                        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Box>
                <Typography variant='caption' color='rgba(244,244,246,0.3)' sx={{ mt: 0.5, display: 'block' }}>
                    {handleFormatDate(item.createdAt)}
                </Typography>
            </Box>
        </Box>
    );

    return (
        <Dialog
            open
            onClose={() => onCloseClick(false)}
            fullWidth
            maxWidth='sm'
            PaperProps={{
                sx: {
                    bgcolor: BG_SECTION,
                    border: BORDER_SUBTLE,
                    borderRadius: '12px',
                    color: TEXT_PRIMARY,
                },
            }}
        >
            {/* ── Título ── */}
            <DialogTitle sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                pb: 1, borderBottom: BORDER_SUBTLE,
            }}>
                <Box>
                    <Typography variant='h6' fontWeight={700} color={TEXT_PRIMARY}>Comments</Typography>
                    <Box sx={{ width: 32, height: 3, bgcolor: BRAND_RED, mt: 0.5 }} />
                </Box>
                <IconButton
                    onClick={() => onCloseClick(false)}
                    size='small'
                    sx={{ color: TEXT_SECONDARY, '&:hover': { color: TEXT_PRIMARY } }}
                >
                    <CloseIcon fontSize='small' />
                </IconButton>
            </DialogTitle>

            {/* ── Lista de comentários ── */}
            <DialogContent sx={{ p: 0 }}>
                <Box
                    id='ul-project-content-comments-modal'
                    sx={{
                        maxHeight: '40vh',
                        overflowY: 'auto',
                        scrollbarWidth: 'thin',
                        scrollbarColor: 'rgba(255,255,255,0.2) transparent',
                        '&::-webkit-scrollbar': { width: '6px' },
                        '&::-webkit-scrollbar-track': { background: 'transparent' },
                        '&::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,0.2)', borderRadius: '3px' },
                        '&::-webkit-scrollbar-button': { display: 'none', width: 0, height: 0 },
                    }}
                >
                    {allComments.length === 0 && !loadingMore && (
                        <Box sx={{ py: 5, textAlign: 'center' }}>
                            <Typography variant='body2' color={TEXT_SECONDARY}>No comments yet</Typography>
                        </Box>
                    )}
                    {allComments.map((item, index) => renderComment(item, index))}
                    {loadingMore && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
                            <CircularProgress size={22} sx={{ color: BRAND_RED }} />
                        </Box>
                    )}
                </Box>
            </DialogContent>

            {/* ── Input de novo comentário ── */}
            <DialogActions sx={{ px: 2, pt: 1.5, pb: 2, gap: 1, borderTop: BORDER_SUBTLE, alignItems: 'flex-end' }}>
                <TextField
                    value={inputComment}
                    onChange={(e) => setInputComment(e.target.value)}
                    placeholder='Write a comment...'
                    multiline
                    rows={2}
                    fullWidth
                    size='small'
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            color: TEXT_PRIMARY,
                            bgcolor: BG_CARD,
                            '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
                            '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },

        '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
            WebkitBoxShadow: `0 0 0 100px #1c1c24 inset`,
            WebkitTextFillColor: '#f4f4f6',
            caretColor: '#f4f4f6',
        },
                            '&.Mui-focused fieldset': { borderColor: 'rgba(255,255,255,0.7)' },
                        },
                    }}
                />
                <Button
                    variant='contained'
                    onClick={handleCreate}
                    disabled={!inputComment.trim()}
                    sx={{
                        bgcolor: BRAND_RED,
                        textTransform: 'none',
                        flexShrink: 0,
                        alignSelf: 'flex-end',
                        '&:hover': { bgcolor: '#a93226' },
                        '&.Mui-disabled': { bgcolor: 'rgba(192,57,43,0.4)', color: 'rgba(255,255,255,0.4)' },
                    }}
                >
                    Comment
                </Button>
            </DialogActions>
        </Dialog>
    );
}
